import React, { useEffect, useState } from "react";
import axiosInstance from "../../../../../services/AxiosInstance";
import { notifyError } from "../../components/CitoPlusToastr";
import moment from "moment";

const HISTORY_URL = "/api/v3/zonas-sociales/activos/historial/";

const ActivoHistoryModal = ({ show, onHide, activo }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show && activo) {
      fetchHistory();
    } else {
      setHistory([]);
    }
  }, [show, activo]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(HISTORY_URL, {
        params: { activo_id: activo.id },
      });
      const results = Array.isArray(response.data)
        ? response.data
        : response.data?.results || [];
      setHistory(results);
    } catch (error) {
      notifyError("No fue posible cargar el historial del activo.");
    } finally {
      setLoading(false);
    }
  };

  const getActionBadge = (action) => {
    const config = {
      create: {
        bg: "bg-success",
        text: "text-white",
        icon: "fa-plus",
        label: "Creado",
      },
      update: {
        bg: "bg-info",
        text: "text-white",
        icon: "fa-pen",
        label: "Actualizado",
      },
      delete: {
        bg: "bg-danger",
        text: "text-white",
        icon: "fa-trash",
        label: "Eliminado",
      },
    };
    return (
      config[action] || {
        bg: "bg-secondary",
        text: "text-white",
        icon: "fa-circle",
        label: action,
      }
    );
  };

  const formatSnapshot = (snapshot) => {
    if (!snapshot) return null;
    const excluded = ["id", "perfil_id", "created_at", "updated_at", "activo"];
    const entries = Object.entries(snapshot).filter(
      ([key]) => !excluded.includes(key),
    );
    if (entries.length === 0) return null;

    return (
      <div className="mt-2">
        <small className="text-muted text-uppercase fw-bold mb-1 d-block">
          Cambios:
        </small>
        <ul className="list-group list-group-flush small">
          {entries.map(([key, value]) => {
            const displayValue =
              typeof value === "object"
                ? value?.nombre || JSON.stringify(value)
                : String(value);
            return (
              <li
                key={key}
                className="list-group-item d-flex justify-content-between py-1 px-0 bg-transparent"
              >
                <span className="text-muted text-capitalize">
                  {key.replace(/_/g, " ")}
                </span>
                <span className="fw-medium">{displayValue}</span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  if (!show) return null;

  return (
    <>
      <div
        className="modal fade show"
        style={{ display: "block", zIndex: 1060 }}
        tabIndex="-1"
        role="dialog"
      >
        <div
          className="modal-dialog modal-dialog-centered modal-lg"
          role="document"
        >
          <div className="modal-content">
            <div className="modal-header border-bottom-0 pb-0">
              <div>
                <h5 className="modal-title">
                  <i className="fas fa-history text-primary me-2" />
                  Historial del Activo
                </h5>
                <p className="text-muted mb-0 small">
                  <strong>{activo?.nombre}</strong> (#{activo?.consecutivo})
                </p>
              </div>
              <button type="button" className="btn-close" onClick={onHide} />
            </div>

            <div
              className="modal-body"
              style={{ maxHeight: "60vh", overflowY: "auto" }}
            >
              {loading ? (
                <div className="text-center py-4">
                  <div
                    className="spinner-grow text-primary"
                    role="status"
                    style={{ width: "2rem", height: "2rem" }}
                  >
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                  <p className="mt-2 text-muted">Cargando historial...</p>
                </div>
              ) : history.length === 0 ? (
                <div className="text-center py-5">
                  <div className="mb-3">
                    <i className="fas fa-clipboard-list fa-3x text-secondary opacity-25" />
                  </div>
                  <h6 className="text-muted">Sin historial</h6>
                  <p className="text-muted small mb-0">
                    No hay cambios registrados para este activo.
                  </p>
                </div>
              ) : (
                <div className="position-relative ps-4">
                  {history.map((record, index) => {
                    const badge = getActionBadge(record.action);
                    return (
                      <div key={record.id} className="position-relative pb-3">
                        {index !== history.length - 1 && (
                          <div
                            className="position-absolute"
                            style={{
                              left: "-19px",
                              top: "28px",
                              bottom: "-12px",
                              width: "2px",
                              background: "#e9ecef",
                            }}
                          />
                        )}
                        <div className="d-flex gap-3">
                          <div
                            className={`rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 ${badge.bg} ${badge.text}`}
                            style={{ width: "38px", height: "38px" }}
                          >
                            <i
                              className={`fas ${badge.icon}`}
                              style={{ fontSize: "14px" }}
                            />
                          </div>
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <span
                                  className={`badge ${badge.bg} ${badge.text} me-2`}
                                >
                                  {badge.label}
                                </span>
                                <span className="text-muted small">
                                  <i className="far fa-clock me-1" />
                                  {moment(record.created_at).format(
                                    "DD MMM YYYY, HH:mm",
                                  )}
                                </span>
                              </div>
                            </div>
                            {(record.snapshot || record.reason) && (
                              <div className="mt-2">
                                {record.snapshot && (
                                  <details className="small">
                                    <summary className="text-primary text-decoration-none cursor-pointer d-inline-flex align-items-center">
                                      <i
                                        className="fas fa-chevron-right me-1"
                                        style={{ fontSize: "10px" }}
                                      />
                                      Ver cambios
                                    </summary>
                                    {formatSnapshot(record.snapshot)}
                                  </details>
                                )}
                                {record.reason && (
                                  <div className="mt-2 p-2 bg-danger bg-opacity-10 rounded border border-danger border-opacity-25">
                                    <small className="text-danger fw-bold">
                                      <i className="fas fa-exclamation-triangle me-1" />
                                      Motivo:
                                    </small>
                                    <p className="mb-0 small text-dark">
                                      {record.reason}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary btn-sm text-white"
                onClick={onHide}
              >
                <i className="fas fa-times me-1" />
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1050 }}
        onClick={onHide}
      />
    </>
  );
};

export default ActivoHistoryModal;
